// Переезд подложки на живой карте: язык кабинета — часть адреса тайла.
//
// Кабинет переключает язык БЕЗ перезагрузки страницы (`setLang` → `persistLang`,
// каталог уже в бандле), а у Яндекса язык подписей стоит в адресе тайла
// (`lang=ru_RU`). Без этого шага капитан, переключившийся на английский, до
// конца сеанса читал бы русские названия под английским интерфейсом — карта
// создана один раз и своих адресов сама не пересматривает.
//
// У Carto язык в адрес не входит вовсе, поэтому там переезда не бывает, и это
// не частный случай, а прямое следствие сравнения адресов.

import type maplibregl from 'maplibre-gl'
import type { BasemapSpec } from './basemap'
import { BASE_SOURCE_ID } from './baseStyle'

/** Разошлись ли адреса тайлов у двух подложек.
 *
 *  «Не трогать, когда адрес тот же» — не оптимизация, а условие: `setTiles`
 *  роняет кэш плиток источника целиком и заставляет перекачать весь видимый
 *  кадр. Позвав его на каждый рендер, мы получили бы карту, мигающую на любое
 *  движение состояния. */
export function basemapTilesChanged(prev: BasemapSpec, next: BasemapSpec): boolean {
  if (prev.tiles.length !== next.tiles.length) return true
  return prev.tiles.some((t, i) => t !== next.tiles[i])
}

/** Пересобрать источник подложки живой карты под новые адреса.
 *
 *  Молчит, когда карты нет, источник ещё не заведён или адреса те же: зовут
 *  это из эффекта, который срабатывает и до готовности стиля. */
export function refreshBasemapSource(
  map: maplibregl.Map | null | undefined,
  next: BasemapSpec,
): boolean {
  if (!map) return false
  const source = map.getSource(BASE_SOURCE_ID) as
    | (maplibregl.RasterTileSource & { tiles?: string[] })
    | undefined
  if (!source || typeof source.setTiles !== 'function') return false
  const current = source.tiles ?? []
  if (current.length === next.tiles.length && current.every((t, i) => t === next.tiles[i])) {
    return false
  }
  source.setTiles([...next.tiles])
  return true
}

/** Живые карты кабинета. Их несколько и они переживают смену языка:
 *  «Модели»/«Карты», «Спутники», «Буи», «Наблюдаемость». Реестр модульный по
 *  той же причине, что и `activeMap`: карты создаёт императивный код, и
 *  прокинуть смену языка пропом значило бы завести четыре способа ошибиться. */
const live = new Set<maplibregl.Map>()

/** Поставить карту на учёт. Возвращает снятие — зовите его в уборке эффекта. */
export function registerBasemapMap(map: maplibregl.Map): () => void {
  live.add(map)
  return () => {
    live.delete(map)
  }
}

/** Довезти новую подложку до всех живых карт. Возвращает, сколько тронуто. */
export function refreshBasemaps(next: BasemapSpec): number {
  let touched = 0
  for (const map of live) if (refreshBasemapSource(map, next)) touched += 1
  return touched
}

/** Только для тестов: сколько карт на учёте. */
export function liveBasemapMaps(): number {
  return live.size
}
